import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { useQuery } from "urql";
import { UncontrolledTooltip } from "reactstrap";

import { priceToString } from "utils/priceToString";
import {
  ProductListCalculationMarker,
  ProductListDownloadButton,
  ProductListFilterButton,
  ProductListSearchControl,
  ProductListSizeSwitcher,
} from "components/ProductListOfProject/SubComponents";
import { Icon } from "components/Icon/Icon";
import { TranslateWrapper } from "components/TranslateWrapper";
import { ProductPriceListQry, UserProductListQry } from "api/queries";
import {
  priceSortingCriteria,
  PRODUCTS_PER_PAGE,
  SORTING_CRITERIA,
} from "variables";
import { useAppBaseContext } from "providers/AppBaseProvider";
import { useItemsPerPage, useTranslate } from "hooks";
import { getCommissionAsProduct } from "utils";
import { useStateLoaderContext } from "providers";
import {
  useIsFetching,
  useProjectCommissioningCost,
  useWizardActions,
} from "../../objs/_ConfiguratorObjs/Wizard/stores/wizardStore";
import { ProductListOfProject } from "./ProductListOfProject";
import { ItemsPerPageSelect } from "../ItemsPerPageSelect";
import { useAuthStore } from "stores/authStore";

export const ProductListWithControls = Scrivito.connect(
  ({
    projectId = null,
    nodeId,
    configurationOption = null,
    hasFilter = false,
    hasSearch = false,
    hasSizeSwitcher = false,
    isModalView = false,
    productListRef = null,
  }) => {
    const translate = useTranslate();
    const { setIsFetching } = useStateLoaderContext();
    const [isOrderDescending, setIsOrderDescending] = useState(false);
    const [sortCriteria, setSortCriteria] = useState(SORTING_CRITERIA.NAME);
    const tableRef = useRef(null);
    const isCommissionIncluded = useMemo(
      () =>
        configurationOption && configurationOption !== "commissionNotIncluded",
      [configurationOption],
    );
    const { getCommissioningCost, resetCommissionCost } = useWizardActions();
    const isFetching = useIsFetching();
    const { lang, isSameProj } = useAppBaseContext();
    const isPriceSortingCriteria = priceSortingCriteria.includes(sortCriteria);
    const { canSeePrices } = useAuthStore.use.currentUser() || {};

    const {
      itemsPerPage,
      handleItemsPerPageChange,
      currentPageNo,
      setCurrentPageNo,
    } = useItemsPerPage(PRODUCTS_PER_PAGE);

    const [{ data: productListData, fetching, stale }, refetchUserProductList] =
      useQuery({
        query: UserProductListQry,
        variables: !isPriceSortingCriteria
          ? {
              input: {
                sort: {
                  sortBy: sortCriteria,
                  sortOrder: isOrderDescending ? "desc" : "asc",
                },
                projectId,
                limit: itemsPerPage,
                page: currentPageNo,
                projectNodeId: nodeId,
                languageCode: lang,
              },
            }
          : {
              input: {
                projectId,
                projectNodeId: nodeId,
                limit: 0,
                languageCode: lang,
              },
            },
        pause: !projectId,
      });

    const productListFetching = fetching || stale;

    const productList = productListData?.productList ?? {};
    const productsDatas = useMemo(
      () => productList?.products ?? {},
      [productList?.products],
    );

    const productsIds = useMemo(
      () => productList?.products?.map((product) => product.id),
      [productList?.products],
    );

    // TODO: This additional request for prices needs for AB
    const [
      {
        data: priceListData,
        stale: priceListStale,
        fetching: priceListFetching,
      },
      refetchProductPriceList,
    ] = useQuery({
      query: ProductPriceListQry,
      variables: isPriceSortingCriteria
        ? {
            input: {
              projectId,
              projectNodeId: nodeId,
              sort: {
                sortBy: sortCriteria,
                sortOrder: isOrderDescending ? "desc" : "asc",
              },

              limit: itemsPerPage,
              page: currentPageNo,
              languageCode: lang,
            },
          }
        : {
            input: {
              projectId,
              projectNodeId: nodeId,
              filter: {
                productNodeIds: productsIds,
              },
              languageCode: lang,
            },
          },
      pause: (!projectId && !productListFetching) || !canSeePrices,
    });

    if (productListRef) {
      productListRef.current = {
        refetchProductPriceList: canSeePrices && refetchProductPriceList,
        refetchUserProductList,
      };
    }

    const priceOnRequest =
      priceListData?.productPriceList?.includesPriceOnRequest;

    const { price, productPriceList } = priceListData ?? {};
    const priceListProducts = useMemo(
      () => productPriceList?.products ?? [],
      [productPriceList?.products],
    );

    const productsWithPrices = useMemo(() => {
      if (
        !productsDatas.length ||
        (!priceListProducts.length && canSeePrices)
      ) {
        return [];
      }

      return productsDatas.map((product) => {
        const matchingPrice = priceListProducts.find(
          (priceItem) => priceItem.id === product.id,
        );

        return {
          ...product,
          priceGroup: matchingPrice?.priceGroup || "",
          priceListDesc: matchingPrice?.priceListDesc || "",
          totalPrice: matchingPrice?.totalPrice || {},
          unitPrice: matchingPrice?.unitPrice || {},
        };
      });
    }, [productsDatas, priceListProducts, canSeePrices]);

    const listToDisplay = useMemo(() => {
      const productsArray = Array.isArray(productsDatas) ? productsDatas : [];
      const priceListArray = Array.isArray(priceListProducts)
        ? priceListProducts
        : [];

      if (isPriceSortingCriteria) {
        return priceListArray.map((priceProduct) => {
          const matchingProduct = productsArray.find(
            (product) => product.id === priceProduct.id,
          );

          return {
            ...priceProduct,
            title: matchingProduct?.title ?? "",
            presentation: matchingProduct?.presentation ?? "",
            quantity: matchingProduct?.quantity ?? 0,
          };
        });
      }

      return productsWithPrices;
    }, [
      isPriceSortingCriteria,
      priceListProducts,
      productsDatas,
      productsWithPrices,
    ]);

    const pagesCountForPriceList = Math.ceil(
      productsDatas.length / itemsPerPage,
    );

    const { productsCount = 0, pages: pagesCount = 0 } = productList ?? {}; // "?? {}" because sometimes productList is null

    useEffect(
      () => setIsFetching(priceListStale),
      [priceListStale, setIsFetching],
    );

    const isDownloadAllowed = useMemo(
      () => priceListProducts.length > 0,
      [priceListProducts.length],
    );

    const {
      price: commissioningPrice = null,
      currency: commissioningCurrency = "",
    } = useProjectCommissioningCost();

    useEffect(() => {
      if (isCommissionIncluded) {
        getCommissioningCost(configurationOption);
      }
    }, [isCommissionIncluded, getCommissioningCost, configurationOption]);

    const commissioningProduct = useMemo(
      () =>
        nodeId && isCommissionIncluded
          ? getCommissionAsProduct({
              title: `${translate.stringOnly("WIZARD_COMMISSIONING", null)}
              ${translate(
                configurationOption === "RoomControl_PreConf"
                  ? "WIZARD_COMMISSIONING_PRE_CONF"
                  : "WIZARD_COMMISSIONING_ON_SITE_CONF",
                null,
                true,
              )}
              `,
              centAmount: commissioningPrice,
              currency: commissioningCurrency,
            })
          : null,
      [
        commissioningCurrency,
        commissioningPrice,
        configurationOption,
        isCommissionIncluded,
        nodeId,
        translate,
      ],
    );

    const productsPrice = useMemo(
      () => productPriceList?.productsPrice ?? null,
      [productPriceList?.productsPrice],
    );

    const totalPrice = useMemo(() => {
      if (price || productsPrice) {
        return priceToString({
          price: {
            currencyCode: productsPrice?.currencyCode || price?.currencyCode,
            fractionDigits:
              productsPrice?.fractionDigits || price?.fractionDigits,
            centAmount: productsPrice?.centAmount || price?.centAmount,
          },
          noPriceText: null,
          commissioningPrice,
        });
      }
    }, [commissioningPrice, price, productsPrice]);

    useEffect(
      () => () => {
        resetCommissionCost();
      },
      [resetCommissionCost],
    );

    const totalPriceLabel = !isSameProj
      ? translate("TOTAL_PRICE")
      : translate("TOTAL_PRICE_SAME");

    return (
      <>
        {isModalView && productList?.includesDummyProduct && (
          <ProductListCalculationMarker />
        )}
        <div
          className={cx({
            "pb-6 mt-3": !isModalView,
            "pb-0 mt-1": isModalView,
          })}
        >
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center font-bold">
              {hasFilter && <ProductListFilterButton />}
              {hasSearch && <ProductListSearchControl />}
              {hasSizeSwitcher && <ProductListSizeSwitcher />}
            </div>
            {canSeePrices === false && (
              <div className="d-flex align-items-center me-2 px-2 bg-warning rounded">
                {translate("VERIFICATION_WARNING")}
              </div>
            )}
            <div className="d-flex align-items-center">
              <div className="d-flex flex-column">
                <div className="me-3 d-flex align-items-center">
                  {totalPriceLabel}:
                  {priceListStale || isFetching ? (
                    <div
                      style={{
                        width: "80px",
                        height: "18px",
                      }}
                      className="blockSkeleton ms-2_5 rounded"
                    ></div>
                  ) : (
                    <>
                      {totalPrice ? (
                        <div
                          className={cx(
                            "d-flex align-items-center flex-nowrap font-bold ms-1 ",
                            {
                              "text-warning": priceOnRequest,
                            },
                          )}
                        >
                          {totalPrice}
                          <Icon
                            className={cx(
                              "ms-2 text-gray-600 cursor-pointer focus-ring focus-ring-light",
                              {
                                "d-none": !priceOnRequest,
                              },
                            )}
                            id="total_price"
                            tabIndex={-1}
                            iconName="circle-info"
                            prefix="far"
                          />
                          <UncontrolledTooltip
                            target="total_price"
                            placement="top"
                          >
                            {translate("TOTAL_PRICE_ATTENTION")}
                          </UncontrolledTooltip>
                          {typeof translate("TOTAL_PRICE_ATTENTION") ===
                            "object" && (
                            <TranslateWrapper
                              tooltipText="TOTAL_PRICE_ATTENTION"
                              tooltipId="total_price"
                              placement="top"
                            />
                          )}
                        </div>
                      ) : (
                        <div className={cx({ "text-warning": priceOnRequest })}>
                          {translate("NO_PRICE")}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <ProductListDownloadButton
                projectId={projectId}
                downloadAllowed={isDownloadAllowed}
                nodeId={nodeId}
                configurationOption={configurationOption}
              />
            </div>
          </div>
          <div className="pt-2_5 bg-white">
            <ItemsPerPageSelect
              itemsPerPage={itemsPerPage}
              onChange={handleItemsPerPageChange}
            />

            <ProductListOfProject
              currentPageNo={currentPageNo}
              fetchingProductList={
                productListFetching ||
                (canSeePrices && (priceListStale || priceListFetching))
              }
              commissionFetching={isFetching}
              isOrderDescending={isOrderDescending}
              pagesCount={
                isPriceSortingCriteria ? pagesCountForPriceList : pagesCount
              }
              products={listToDisplay}
              commissioningProduct={commissioningProduct}
              productsCount={productsCount}
              setCurrentPageNo={setCurrentPageNo}
              setIsOrderDescending={setIsOrderDescending}
              tableRef={tableRef}
              itemsPerPage={itemsPerPage}
              setSortCriteria={setSortCriteria}
              sortCriteria={sortCriteria}
              isModalView={isModalView}
              priceGroupDescription={
                priceListData?.productPriceList?.description
              }
            />
          </div>
        </div>
      </>
    );
  },
);
